import { useEffect, useRef, useState } from "react";
import type { LiveHeatmapConfig } from "@/features/deployment-dashboard/live-view/config/live-heatmap-config";
import { renderLiveHeatmapLayer } from "@/features/deployment-dashboard/live-view/utils/render-live-heatmap-layer";
import type { LiveHeatmapLayer, LiveTrackPoint } from "@/features/deployment-dashboard/types";
import type { WorldToPixelTransformAsset } from "@/features/deployment-dashboard/widgets/panels/facility-schematic/services/world-to-pixel-transform";

/** Renders the external heat canvas after React has committed the current track snapshot. */
export function useLiveHeatmapLayer(
  transform: Pick<WorldToPixelTransformAsset, "matrix"> | undefined,
  tracks: LiveTrackPoint[],
  mapSize: { width: number; height: number } | undefined,
  config: LiveHeatmapConfig,
  enabled = true
): LiveHeatmapLayer | null {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [heatmapLayer, setHeatmapLayer] = useState<LiveHeatmapLayer | null>(null);
  const mapWidth = mapSize?.width;
  const mapHeight = mapSize?.height;

  useEffect(() => {
    const currentMapSize =
      mapWidth != null && mapHeight != null ? { width: mapWidth, height: mapHeight } : undefined;
    const animationFrame = window.requestAnimationFrame(() => {
      const nextLayer = renderLiveHeatmapLayer(
        transform,
        enabled ? tracks : [],
        currentMapSize,
        config,
        canvasRef.current ?? undefined
      );
      if (nextLayer) canvasRef.current = nextLayer.image;
      setHeatmapLayer(nextLayer);
    });

    return () => window.cancelAnimationFrame(animationFrame);
  }, [transform, tracks, mapWidth, mapHeight, config, enabled]);

  return heatmapLayer;
}
